import { useContext, useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { AuthContext } from "../Context/AuthContext";
import TenantContext from "../Context/TenantContext.jsx";
import { IoClose } from "react-icons/io5";
import { RiLogoutBoxRLine } from "react-icons/ri";

const staticNavLinks = [
  { title: "Home", path: "/" },
  { title: "About Us", path: "/about" },
  { title: "Contact Us", path: "/contact" },
];

const MobileMenu = ({ user, menuItems, isAuthenticated, primaryColor, currentPath, onClose, onLogout, onLogin }) => {
  const isActive = (path) => currentPath === path;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col overflow-y-auto bg-white"
      style={{ height: '100dvh', width: '100vw' }}
    >
      {isAuthenticated ? (
        <>
          {/* Header row: avatar + user info + close button */}
          <div className="flex items-center gap-4 px-5 pt-5 pb-6 shrink-0">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 text-white text-xl font-bold shadow-sm"
              style={{ backgroundColor: primaryColor }}
            >
              {user?.firstName?.charAt(0) || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-gray-900 text-base leading-tight">
                Hi {user?.firstName}!
              </div>
              <div className="text-sm text-gray-500 truncate">{user?.email}</div>
            </div>
            <button
              onClick={onClose}
              aria-label="Close menu"
              type="button"
              className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors shrink-0"
            >
              <IoClose className="w-6 h-6 text-gray-700" />
            </button>
          </div>

          <div className="mx-5 border-t border-gray-100 mb-3" />

          <div className="flex flex-col px-4 gap-0.5 pb-10">
            {/* Static nav links */}
            {staticNavLinks.map((link) => {
              const active = isActive(link.path);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={onClose}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl transition-colors"
                  style={active ? { backgroundColor: `${primaryColor}18`, color: primaryColor } : { color: '#374151' }}
                >
                  <span className="text-base font-medium">{link.title}</span>
                </Link>
              );
            })}

            <div className="border-t border-gray-100 my-2" />

            {/* Profile menu items with image icons */}
            {menuItems.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl transition-colors"
                  style={active ? { backgroundColor: `${primaryColor}18`, color: primaryColor } : { color: '#374151' }}
                >
                  <img
                    src={item.icon}
                    alt={item.title}
                    className="w-5 h-5 shrink-0"
                    style={active ? { filter: 'none', opacity: 1 } : { opacity: 0.55 }}
                  />
                  <span className="text-base font-medium">{item.title}</span>
                </Link>
              );
            })}

            <div className="border-t border-gray-100 my-2" />

            {/* Log Out */}
            <button
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 transition-colors"
              onClick={onLogout}
            >
              <RiLogoutBoxRLine className="w-5 h-5 shrink-0 text-red-400" />
              <span className="text-base font-medium">Log Out</span>
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Header row: close button only */}
          <div className="flex justify-end px-5 pt-5 pb-2 shrink-0">
            <button
              onClick={onClose}
              aria-label="Close menu"
              type="button"
              className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <IoClose className="w-6 h-6 text-gray-700" />
            </button>
          </div>

          <div className="flex flex-col px-4 gap-0.5 pb-6">
            {staticNavLinks.map((link) => {
              const active = isActive(link.path);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={onClose}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl transition-colors"
                  style={active ? { backgroundColor: `${primaryColor}18`, color: primaryColor } : { color: '#374151' }}
                >
                  <span className="text-base font-medium">{link.title}</span>
                </Link>
              );
            })}

            <div className="border-t border-gray-100 my-3" />

            <button
              className="w-full py-3 rounded-2xl text-white text-base font-semibold transition-opacity active:opacity-80"
              style={{ backgroundColor: primaryColor }}
              onClick={onLogin}
            >
              Login
            </button>
          </div>
        </>
      )}
    </div>
  );
};

MobileMenu.propTypes = {
  user: PropTypes.object,
  menuItems: PropTypes.arrayOf(PropTypes.shape({ title: PropTypes.string, icon: PropTypes.string, path: PropTypes.string })),
  isAuthenticated: PropTypes.bool,
  primaryColor: PropTypes.string,
  currentPath: PropTypes.string,
  onClose: PropTypes.func,
  onLogout: PropTypes.func,
  onLogin: PropTypes.func,
};

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const { isAuthenticated, setIsAuthenticated, setUser, setToken } = useContext(AuthContext);
  const { tenant = { name: 'Smart Nurse', logo: '/logo-bw.png' } } = useContext(TenantContext) || {};
  const location = useLocation();
  const navigateTo = useNavigate();

  // Use fullLogo for navbar if available, fallback to branding.logo, tenant.logo, or default
  const navbarLogo = tenant?.branding?.fullLogo || tenant?.branding?.logo || tenant?.logo || '/logo-bw.png';

  const _userRaw = localStorage.getItem("user");
  const user = JSON.parse((_userRaw && _userRaw !== "undefined") ? _userRaw : "{}");

  useEffect(() => {
    const widget = document.querySelector('elevenlabs-convai');
    if (widget) widget.style.visibility = mobileMenuOpen ? 'hidden' : '';
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await axios
      .get(
        import.meta.env.VITE_APP_BASE_API +
          import.meta.env.VITE_APP_API_VERSION +
          "/user/patient/logout",
        {
          withCredentials: true,
        }
      )
      .then((res) => {
        toast.success(res.data.message);
        setIsAuthenticated(false);
        setUser({});
        setToken(null);
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/";
      })
      .catch((err) => {
        toast.error(err.response.data.message);
      });
    setMobileMenuOpen(false);
  };

  const goToLogin = () => {
    navigateTo("/login");
    setMobileMenuOpen(false);
  };

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  const menuItems = [
    { title: "Profile", icon: "/cards/profile.png", path: "/profile" },
    { title: "Vitals", icon: "/cards/patient.png", path: "/vitals" },
    { title: "Nurse Chat", icon: "/cards/voice.png", path: "/dashboard/nurse-chat" },
    { title: "Visuals Data", icon: "/cards/visual.png", path: "/visual-data" },
    { title: "Medications", icon: "/cards/medications.png", path: "/medications" },
    { title: "Share Data", icon: "/cards/share.png", path: "/dashboard/share-data" },
    { title: "Appointments", icon: "/cards/pain.png", path: "/appointments" },
  ];

  // Function to handle back navigation
  const handleGoBack = () => {
    navigateTo(-1);
  };

  return (
    <>
    <header className="drop-shadow-lg bg-white sticky top-0 left-0 z-50">
      <div className="max-w-screen-2xl mx-auto px-4 py-1 flex items-center justify-between">
        {/* Back button for mobile and Logo for desktop */}
        <div className="flex-shrink-0">
          {/* Logo - visible only on desktop */}
          <div className="hidden lg:block">
      <Link to="/">
              <img
        alt={`${tenant.name || 'Smart Nurse'} Logo`}
                width="120"
                height="38"
                decoding="async"
                style={{ color: "transparent" }}
        src={navbarLogo}
        onError={(e)=>{ e.currentTarget.src='/logo-bw.png'; }}
              />
            </Link>
          </div>
          
          {/* Back button - visible only on mobile */}
          <button 
            onClick={handleGoBack}
            className="lg:hidden flex items-center justify-center border border-black/1 w-10 h-10 rounded-full bg-white shadow-md hover:shadow-lg transition-shadow"
            aria-label="Go back"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M19 12H5"/>
              <path d="M12 19l-7-7 7-7"/>
            </svg>
          </button>
        </div>
        
        {/* Centered logo for mobile view */}
        <div className="lg:hidden flex-1 flex justify-center">
          <Link to="/">
            <img
              alt={`${tenant.name || 'Smart Nurse'} Logo`}
              width="100"
              height="32"
              decoding="async"
              style={{ color: "transparent" }}
              src={navbarLogo}
              onError={(e)=>{ e.currentTarget.src='/logo-bw.png'; }}
            />
          </Link>
        </div>
        
        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-8">
          <nav className="relative z-10 flex-1 items-center justify-center bg-white">
            <ul className="flex list-none items-center space-x-1">
              <li>
                <Link
                  to="/"
                  className={`inline-flex h-9 w-max items-center justify-center px-4 py-2 text-md font-medium transition-colors hover:text-primary focus:outline-none border-b-2 ${
                    isActiveRoute('/') 
                      ? 'border-blue-600 text-xl' 
                      : 'border-transparent'
                  }`}
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/appointments"
                  className={`inline-flex h-9 w-max items-center justify-center px-4 py-2 text-md font-medium transition-colors hover:text-primary focus:outline-none border-b-2 ${
                    isActiveRoute('/appointments')
                      ? 'border-blue-600 text-xl'
                      : 'border-transparent'
                  }`}
                >
                  Appointments
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className={`inline-flex h-9 w-max items-center justify-center px-4 py-2 text-md font-medium transition-colors hover:text-primary focus:outline-none border-b-2 ${
                    isActiveRoute('/about')
                      ? 'border-blue-600 text-xl'
                      : 'border-transparent'
                  }`}
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className={`inline-flex h-9 w-max items-center justify-center px-4 py-2 text-md font-medium transition-colors hover:text-primary focus:outline-none border-b-2 ${
                    isActiveRoute('/contact')
                      ? 'border-blue-600 text-xl'
                      : 'border-transparent'
                  }`}
                >
                  Contact Us
                </Link>
              </li>
            </ul>
          </nav>
        </div>
        
        {/* Desktop profile/login */}
        <div className="hidden lg:flex items-center">
          {isAuthenticated ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="flex items-center gap-2"
              >
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white text-lg font-semibold">
                  {user?.firstName?.charAt(0) || "U"}
                </div>
              </button>

              {/* Dropdown Menu */}
              {showProfileDropdown && (
                <div className="absolute right-0 mt-2 bg-white divide-y divide-gray-100 rounded-lg shadow-lg w-64 z-50">
                  <div className="px-4 py-3 text-sm text-gray-900">
                    <div className="font-semibold">{user?.firstName} {user?.lastName}</div>
                    <div className="truncate text-gray-500">{user?.email}</div>
                  </div>
                  
                  <ul className="py-2 text-gray-700">
                    {menuItems.map((item, index) => (
                      <li key={index}>
                        <Link
                          to={item.path}
                          className="flex items-center px-4 py-2 hover:bg-gray-50 transition-colors"
                          onClick={() => setShowProfileDropdown(false)}
                        >
                          <span className="w-5 h-5 mr-3 flex items-center justify-center">
                            <img src={item.icon} alt={item.title} className="w-5 h-5" />
                          </span>
                          {item.title}
                        </Link>
                      </li>
                    ))}
                  </ul>

                  <div className="py-1">
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium bg-blue-600 text-white shadow hover:bg-blue-700 h-10 rounded-full px-8 transition-colors"
              onClick={goToLogin}
            >
              Login
            </button>
          )}
        </div>
        
        {/* Mobile hamburger */}
        <div className="block lg:hidden">
          <button
            className="inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2 bg-white"
            type="button"
            aria-haspopup="dialog"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((v) => !v)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="size-5"
            >
              <line x1="4" x2="20" y1="12" y2="12"></line>
              <line x1="4" x2="20" y1="6" y2="6"></line>
              <line x1="4" x2="20" y1="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>
      
    </header>

    {/* Mobile menu — sibling of header so sticky stacking context doesn't clip it */}
    {mobileMenuOpen && (
      <MobileMenu
        user={user}
        menuItems={menuItems}
        isAuthenticated={isAuthenticated}
        primaryColor={tenant?.branding?.primaryColor || '#4B6CB7'}
        currentPath={location.pathname}
        onClose={() => setMobileMenuOpen(false)}
        onLogout={handleLogout}
        onLogin={goToLogin}
      />
    )}
    </>
  );
};

export default Navbar;
